import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DealFinderApi } from '@model/iceberg/service/deal-finder/deal-finder-api';
import { PackageReferencesActions } from '@state/slices/app';
import { getErrorCode } from '@util/error';
import { TourActions } from '@state/slices/tours/tourActions';

export enum TourReferencesActions {
  SET_TOUR_REFERENCES = '@TOURS/setTourReferences',
  UNSET_TOUR_REFERENCES = '@TOURS/unsetTourReferences',
  GET_FROM_TOUR_REFERENCE = '@TOURS/fetchFromTourReference',
  FROM_TOUR_REFERENCE_SUCCESS = '@TOURS/FROM_TOUR_REFERENCE_SUCCESS'
}

export interface TourReferencesState {
  packageReferences: Array<string>;
  fromTourReference: string;
  leadInTourReferences: Array<string>;
  error: string | null;
  isLoading: boolean;
  isFromTourReferenceLoading: boolean;
}

export interface SetTourReferencesPayload {
  packageReferences: Array<string>;
  searchToken?: string;
}

export interface SetPackagePayload {
  packageReferences: Array<string>;
  searchToken: string;
}

export interface TourReferencesSuccessPayload {
  packageReferences: Array<string>;
}

export const INITIAL_TOUR_REFERENCES_STATE: TourReferencesState = {
  packageReferences: [],
  error: null,
  fromTourReference: '',
  leadInTourReferences: [],
  isLoading: false,
  isFromTourReferenceLoading: false
};

export const setTourReferences = createAsyncThunk<
  TourReferencesSuccessPayload,
  SetTourReferencesPayload,
  { rejectValue: string }
>(TourReferencesActions.SET_TOUR_REFERENCES, async (payload, { rejectWithValue }) => {
  const { packageReferences, searchToken } = payload;

  if (!searchToken) {
    return { packageReferences };
  }

  try {
    const api = new DealFinderApi();
    await api.reference({ packageReferences, searchToken });
    return { packageReferences };
  } catch (error: any) {
    return rejectWithValue(getErrorCode(error));
  }
});

export const fetchFromTourReference = createAsyncThunk<string, string, { rejectValue: string }>(
  TourReferencesActions.GET_FROM_TOUR_REFERENCE,
  async (payload, { rejectWithValue }) => {
    try {
      const api = new DealFinderApi();
      const response = await api.fromTourReference(payload);

      if (response.searchToken) {
        return response.searchToken;
      } else {
        return rejectWithValue('No search token received');
      }
    } catch (error: any) {
      return rejectWithValue(getErrorCode(error));
    }
  }
);

const tourReferencesSlice = createSlice({
  name: TourActions.SLICE_NAME,
  initialState: INITIAL_TOUR_REFERENCES_STATE,
  reducers: {
    unsetTourReferences: (state) => {
      state.packageReferences = INITIAL_TOUR_REFERENCES_STATE.packageReferences;
      state.error = null;
      state.isLoading = false;
    },
    setLeadInTourReferences: (state, action: PayloadAction<TourReferencesSuccessPayload>) => {
      state.leadInTourReferences = action.payload.packageReferences;
    },
    unsetLeadInTourReferences: (state) => {
      state.leadInTourReferences = INITIAL_TOUR_REFERENCES_STATE.leadInTourReferences;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(setTourReferences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.packageReferences = INITIAL_TOUR_REFERENCES_STATE.packageReferences;
      })
      .addCase(setTourReferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.packageReferences = action.payload.packageReferences;
      })
      .addCase(setTourReferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to set tour references';
        state.packageReferences = INITIAL_TOUR_REFERENCES_STATE.packageReferences;
      })
      .addCase(fetchFromTourReference.pending, (state) => {
        state.isFromTourReferenceLoading = true;
        state.error = null;
      })
      .addCase(fetchFromTourReference.fulfilled, (state, action) => {
        state.isFromTourReferenceLoading = false;
        state.error = null;
        state.fromTourReference = action.payload;
        state.packageReferences = [action.meta.arg];
      })
      .addCase(fetchFromTourReference.rejected, (state, action) => {
        state.isFromTourReferenceLoading = false;
        state.error = action.payload || 'Failed to get from tour reference';
        state.fromTourReference = '';
      })
      .addCase(PackageReferencesActions.SET_PACKAGE_REFERENCES, (state) => {
        state.packageReferences = INITIAL_TOUR_REFERENCES_STATE.packageReferences;
        state.error = null;
        state.isLoading = false;
      });
  }
});

export const { unsetTourReferences, setLeadInTourReferences, unsetLeadInTourReferences } = tourReferencesSlice.actions;

export const tourReferencesReducer = tourReferencesSlice.reducer;
